import { env } from "@/config/env"
import {
  parseSubscriptionStartedAtMs,
  type UserSubscription,
} from "@/features/subscriptions"
import {
  SUBSCRIPTION_PLAN_IDS_WITH_INCLUDED_ASTRO_MAP_REPORT,
  SUBSCRIPTION_PLAN_IDS_WITH_INCLUDED_AURA_SCAN_REPORT,
  SUBSCRIPTION_PLAN_IDS_WITH_INCLUDED_SOULMATE_REPORT,
} from "@/features/subscriptions/report-plan-codes"

import { ReportIds, type ReportId } from "../config/reports-data"
import { syncIncludedReportUnlock } from "../hooks/report-unlock-store"

const REPORTS_BUNDLE = env.VITE_SUBSCRIPTION_BUNDLE

function findIncludedSubscription(
  subscriptions: UserSubscription[],
  planIds: readonly string[],
): UserSubscription | null {
  const matches = subscriptions.filter((subscription) => {
    const bundleMatches = REPORTS_BUNDLE ? subscription.bundle === REPORTS_BUNDLE : true
    return (
      bundleMatches &&
      planIds.includes(subscription.planCode) &&
      subscription.status !== "expired"
    )
  })
  if (matches.length === 0) return null
  // Prefer the most recent included plan if several exist.
  return matches.sort(
    (a, b) =>
      parseSubscriptionStartedAtMs(b.startedAt) - parseSubscriptionStartedAtMs(a.startedAt),
  )[0]
}

function syncFromSubscription(reportId: ReportId, subscription: UserSubscription): void {
  // `started_at` is UTC without a zone suffix — adjust for client offset (lpr-web).
  const startedAtMs = parseSubscriptionStartedAtMs(subscription.startedAt)
  syncIncludedReportUnlock(reportId, startedAtMs)
}

/**
 * Sync Soulmate / Astro Map / Aura Scan unlock timers from the matching
 * included-plan subscription's `startedAt`. Remaining = startedAt + 30‑min
 * (or 10‑min after liked review) − now. No-op when the user has no matching
 * subscription.
 */
export function startIncludedReportUnlocksIfNeeded(
  subscriptions: UserSubscription[],
): void {
  const soulmateSub = findIncludedSubscription(
    subscriptions,
    SUBSCRIPTION_PLAN_IDS_WITH_INCLUDED_SOULMATE_REPORT,
  )
  if (soulmateSub) syncFromSubscription(ReportIds.Soulmate, soulmateSub)

  const astroSub = findIncludedSubscription(
    subscriptions,
    SUBSCRIPTION_PLAN_IDS_WITH_INCLUDED_ASTRO_MAP_REPORT,
  )
  if (astroSub) syncFromSubscription(ReportIds.AstroMap, astroSub)

  const auraSub = findIncludedSubscription(
    subscriptions,
    SUBSCRIPTION_PLAN_IDS_WITH_INCLUDED_AURA_SCAN_REPORT,
  )
  if (auraSub) syncFromSubscription(ReportIds.AuraScan, auraSub)
}
